import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {DEFAULT_LOCALE, I18N_REDUCER_STATE_ID} from './i18n_constants';
import {ApplicationLocale, LocaleMessagesType} from 'src/js/common/types';
import {I18nConfigRaw} from 'src/js/model/i18n_config';

export interface I18nState {
  i18nConfig: I18nConfigRaw | undefined;
  localeMessages: Record<ApplicationLocale, LocaleMessagesType>;
  currentLocale: ApplicationLocale;
  isInitialized: boolean;
  isDynamicTranslationInitialized: Record<string, boolean>;
  dynamicLoaders?: string[];
}

const initialState: I18nState = {
  i18nConfig: undefined,
  localeMessages: {
    cs: {},
    de: {},
    en: {},
    sk: {},
  },
  currentLocale: DEFAULT_LOCALE,
  isInitialized: false,
  isDynamicTranslationInitialized: {},
  dynamicLoaders: undefined,
};

export interface SetConfigPayload {
  config: I18nConfigRaw;
  reload?: boolean;
}

export interface AddLocaleMessagesPayload {
  messages: LocaleMessagesType;
  locale: ApplicationLocale;
  id: string;
}

export interface ChangeCurrentLocalePayload {
  locale: ApplicationLocale;
}

export interface InitDynamicLoadersPayload {
  loaders: string[];
}

export const i18nSlice = createSlice({
  name: I18N_REDUCER_STATE_ID,
  initialState,
  reducers: {
    setConfig: (state, action: PayloadAction<SetConfigPayload>) => {
      const {config, reload = false} = action.payload;
      if (reload || config) {
        state.i18nConfig = config;
      }
    },
    addLocaleMessages: (
      state,
      action: PayloadAction<AddLocaleMessagesPayload>
    ) => {
      const {messages, locale} = action.payload;
      if (messages) {
        state.localeMessages[locale] = {
          ...state.localeMessages[locale],
          ...messages,
        };
        state.isDynamicTranslationInitialized[action.payload.id] = true;
      }
    },
    changeCurrentLocale: (
      state,
      action: PayloadAction<ChangeCurrentLocalePayload>
    ) => {
      state.currentLocale = action.payload.locale;
    },
    setIsInitialized: (state) => {
      state.isInitialized = true;
    },
    initDynamicLoaders: (
      state,
      action: PayloadAction<InitDynamicLoadersPayload>
    ) => {
      state.dynamicLoaders = action.payload.loaders;
      state.dynamicLoaders?.forEach((loader) => {
        state.isDynamicTranslationInitialized[loader] = false;
      });
    },
  },
});
